import React, { useId, useMemo, useState } from 'react';
import { ClassifierData } from 'types/classifiers';
import { ContactDto } from 'types/contacts.ts';

import StatusChip from 'components/@extended/StatusChip.tsx';
import { buttonStyle } from 'components/buttons/buttonStyle';
import GridItemList, { infoLabel, Row } from 'components/GridItemList.tsx';
import { HistoryTimeline } from 'components/history/HistoryTimeline';
import Loader from 'components/Loader.tsx';
import { FormattedMessage } from 'react-intl';
import {
    CheckStatusType,
    CheckType,
    ComplianceBlockHistoryDto,
    ComplianceBlockingSeverity,
    ComplianceBlockStatus,
    PartyComplianceCheckDto,
    PartyRiskFactorDto,
} from 'types/compliance.ts';
import {
    getCheckStatusChipType,
    getRiskProfileStatusChipType,
    getVerificationStatusChipType,
} from '../../compliance/ComplianceStatus.tsx';

import { LabelCard } from 'components/cards/LabelCard.tsx';
import { ErrorOutline as ErrorOutlineIcon } from 'components/icons';
import InfoTooltip from 'components/InfoTooltip.tsx';
import { FormattedDate } from 'components/misc/FormattedDate.tsx';
import { useModal } from 'components/modals/useModal.ts';
import NoData from 'components/NoData.tsx';
import { RecordLink } from 'components/record-chip/RecordLink';
import StatusIcon from 'components/status/StatusIcon.tsx';
import { Alert } from 'components/ui/alert';
import { Button } from 'components/ui/button';
import { Checkbox } from 'components/ui/checkbox';
import { format } from 'date-fns';
import { useEffectiveDateFormat } from 'hooks/useEffectiveDateFormat.ts';
import { useAcknowledgeComplianceBlockMutation, useGetComplianceBlockHistoryQuery } from 'store/api/contacts.ts';
import { getOptionsFromClassifier } from 'utils/formUtil';
import { getClassifierText, t } from 'utils/getTranslations.ts';
import { useClassifiers } from 'utils/useClassifiers.ts';
import ComplianceCheckModal from '../../compliance/ComplianceCheckModal.tsx';
import { isRowEnforcing } from '../blocking/BlockingPolicyReadonlyShared';
import { AcknowledgeBlockModal } from '../blocking/ComplianceActiveRules.tsx';
import { ContactSection } from '../form-sections/ContactSection';

const DEFAULT_VALUE = '-';
// `entity-link` is a targeting hook: the detail row colours it as a link at rest (index.css).
const LINK_CLASS = 'entity-link text-fg hover:text-link w-fit no-underline hover:underline';
const CAPTION_HEADING_CLASS =
    'text-caption new:text-label text-fg-secondary mt-4 block font-bold new:font-semibold uppercase new:tracking-[0.06em]';

interface ComplianceViewProps {
    contact: ContactDto;
    refetchContact: () => Promise<unknown>;
}

const ComplianceView: React.FC<ComplianceViewProps> = ({ contact, refetchContact }) => {
    const activeRules = contact.complianceBlockActiveRules ?? [];
    const hasActiveRules = activeRules.length > 0;

    // The compliance fields' Edit sits above the tab; the checks keep their own add and edit.
    return (
        <ContactSection id="compliance" titleId="contacts.compliance.title" contact={contact}>
            {(edit) => (
                <>
                    {edit ? <div className="mb-4 flex justify-end">{edit}</div> : null}
                    <div className="new:gap-(--card-gap) grid grid-cols-1 gap-6 sm:grid-cols-12">
                        <div className="sm:col-span-2">
                            <ComplianceDashboardCardsVertical contact={contact} />
                        </div>
                        <div className="new:gap-(--card-gap) flex flex-col gap-6 sm:col-span-10">
                            {hasActiveRules && <ComplianceBlockingBanner contact={contact} />}
                            <div className="new:gap-(--card-gap) flex flex-col gap-6 sm:flex-row">
                                <div className="min-w-0 flex-1">
                                    <ComplianceChecks refetchContact={refetchContact} contact={contact} />
                                </div>
                                <div className="min-w-0 flex-1">
                                    <ComplianceBlockHistory partyId={contact.id} />
                                </div>
                            </div>
                        </div>
                    </div>
                </>
            )}
        </ContactSection>
    );
};

export default ComplianceView;

/** The compliance form's values as one list, for a contact the Compliance tab is not shown for. */
export const ComplianceSummary: React.FC<{ contact: ContactDto; button?: React.ReactNode }> = ({ contact, button }) => {
    const { classifiers, isLoading } = useClassifiers({
        codes: ['RISK_PROFILE_TYPE', 'COMPLIANCE_VERIFICATION_STATUS_TYPE', 'RISK_FACTOR_TYPE_CODE'],
    });

    if (isLoading) {
        return <Loader />;
    }

    const yesNo = (value: boolean | null | undefined) => t(value ? 'yes' : 'no');
    // The manual factors are the form field's values, in its order; the automatic ones, which it cannot hold, follow.
    const held = new Set<string>((contact.riskFactors ?? []).map((factor) => factor.riskFactorTypeCode));
    const factors = getOptionsFromClassifier<string, string>(classifiers['RISK_FACTOR_TYPE_CODE']).filter((option) =>
        held.has(option.value)
    );
    const isAutomatic = (option: (typeof factors)[number]) => option.parameters?.['isAutomatic'] === true;
    // Each row under its field's label and hint, in the compliance form's order.
    const rows: Row[] = [
        {
            label: infoLabel('contacts.verification-status', 'contacts.tooltips.verification-status'),
            content:
                getClassifierText(
                    classifiers['COMPLIANCE_VERIFICATION_STATUS_TYPE'],
                    contact.complianceVerificationStatusTypeCode
                ) || DEFAULT_VALUE,
        },
        {
            label: infoLabel('contacts.verification-notes', 'contacts.tooltips.verification-notes'),
            content: contact.complianceVerificationNotes || DEFAULT_VALUE,
            multiline: true,
        },
        {
            label: infoLabel('contacts.risk-profile', 'contacts.tooltips.risk-profile'),
            content: getClassifierText(classifiers['RISK_PROFILE_TYPE'], contact.riskProfileTypeCode) || DEFAULT_VALUE,
        },
        {
            label: infoLabel('contacts.risk-profile-notes', 'contacts.tooltips.risk-profile-notes'),
            content: contact.riskProfileNotes || DEFAULT_VALUE,
            multiline: true,
        },
        {
            label: infoLabel('contacts.risk-factors', 'contacts.tooltips.risk-factors'),
            content: DEFAULT_VALUE,
            chips: [...factors.filter((option) => !isAutomatic(option)), ...factors.filter(isAutomatic)].map(
                (option) => ({ key: option.value, label: option.label })
            ),
        },
        {
            label: infoLabel(
                'contacts.service-agreement-not-needed',
                'contacts.tooltips.is-service-agreement-not-needed'
            ),
            content: yesNo(contact.isServiceAgreementNotNeeded),
        },
        {
            label: infoLabel('contacts.persona-non-grata', 'contacts.tooltips.persona-non-grata'),
            content: yesNo(contact.isPersonaNonGrata),
        },
    ];

    return <GridItemList isNew titleId="contacts.compliance.title" data={rows} button={button} />;
};

/* ── Dashboard cards ─────────────────────────────────────────── */

const ComplianceDashboardCardsVertical: React.FC<{ contact: ContactDto }> = ({ contact }) => {
    const { classifiers, isLoading } = useClassifiers({
        codes: ['RISK_PROFILE_TYPE', 'COMPLIANCE_VERIFICATION_STATUS_TYPE', 'RISK_FACTOR_TYPE_CODE'],
    });

    if (isLoading) {
        return <Loader />;
    }

    const classifierValues = classifiers['RISK_FACTOR_TYPE_CODE']?.values ?? [];
    const classifierOrderMap = new Map(classifierValues.map((v) => [v.code, v.orderNum ?? 999]));

    const sortedFactors = (contact.riskFactors ?? []).toSorted((a, b) => {
        const orderDiff =
            (classifierOrderMap.get(a.riskFactorTypeCode) ?? 999) -
            (classifierOrderMap.get(b.riskFactorTypeCode) ?? 999);
        if (orderDiff !== 0) return orderDiff;
        const textA = getClassifierText(classifiers['RISK_FACTOR_TYPE_CODE'], a.riskFactorTypeCode) ?? '';
        const textB = getClassifierText(classifiers['RISK_FACTOR_TYPE_CODE'], b.riskFactorTypeCode) ?? '';
        return textA.localeCompare(textB);
    });

    const verificationChipType = getVerificationStatusChipType(contact.complianceVerificationStatusTypeCode);
    const riskChipType = getRiskProfileStatusChipType(contact.riskProfileTypeCode);
    const pngChipType = contact.isPersonaNonGrata ? ('error' as const) : ('success' as const);

    return (
        <div className="new:gap-(--card-gap) flex flex-col gap-4">
            <LabelCard
                labelId="compliance.status"
                tooltipText="contacts.tooltips.verification-status"
                content={
                    <>
                        <StatusChip
                            label={
                                getClassifierText(
                                    classifiers['COMPLIANCE_VERIFICATION_STATUS_TYPE'],
                                    contact.complianceVerificationStatusTypeCode
                                ) || DEFAULT_VALUE
                            }
                            type={verificationChipType}
                            className="new:leading-[0.875] text-base font-bold"
                        />
                        {contact.complianceVerificationNotes && (
                            <>
                                <span className={CAPTION_HEADING_CLASS}>
                                    <FormattedMessage id="compliance.notes" />
                                </span>
                                <p className="text-body2 text-fg-secondary font-normal">
                                    {contact.complianceVerificationNotes}
                                </p>
                            </>
                        )}
                    </>
                }
                color="info"
            />
            <RiskWidget
                contact={contact}
                classifiers={classifiers}
                sortedFactors={sortedFactors}
                riskChipType={riskChipType}
            />
            <LabelCard
                labelId="contacts.persona-non-grata"
                content={
                    <StatusChip
                        label={contact.isPersonaNonGrata ? <FormattedMessage id="yes" /> : <FormattedMessage id="no" />}
                        type={pngChipType}
                        className="new:leading-[0.875] text-base font-bold"
                    />
                }
                color="info"
            />
        </div>
    );
};

const RiskWidget: React.FC<{
    contact: ContactDto;
    classifiers: Record<string, ClassifierData>;
    sortedFactors: PartyRiskFactorDto[];
    riskChipType: 'success' | 'error' | 'warning';
}> = ({ contact, classifiers, sortedFactors, riskChipType }) => {
    return (
        <section className="border-divider bg-paper new:border-0 new:shadow-card new:outline-hidden rounded-[7px] border px-4 py-3">
            <div className="flex gap-1">
                <div className="text-body1">
                    <FormattedMessage id="compliance.risk" />
                </div>
                <InfoTooltip text="contacts.tooltips.risk-profile" />
            </div>
            <StatusChip
                label={
                    getClassifierText(classifiers['RISK_PROFILE_TYPE'], contact.riskProfileTypeCode) || DEFAULT_VALUE
                }
                type={riskChipType}
                className="new:leading-[0.875] text-base font-bold"
            />
            <span className={CAPTION_HEADING_CLASS}>
                <FormattedMessage id="compliance.risk-factors" />
            </span>
            {sortedFactors.length > 0 ? (
                <div className="mt-1 flex flex-col items-start gap-1">
                    {sortedFactors.map((factor) => (
                        <StatusChip
                            key={factor.id}
                            label={getClassifierText(classifiers['RISK_FACTOR_TYPE_CODE'], factor.riskFactorTypeCode)}
                            type={getRiskProfileStatusChipType(factor.matchingRiskProfileTypeCode)}
                        />
                    ))}
                </div>
            ) : (
                <span className="text-caption text-fg-secondary">
                    <FormattedMessage id="compliance.no-risk-factors" />
                </span>
            )}
            {contact.riskProfileNotes && (
                <>
                    <span className={CAPTION_HEADING_CLASS}>
                        <FormattedMessage id="compliance.notes" />
                    </span>
                    <p className="text-body2 text-fg-secondary">{contact.riskProfileNotes}</p>
                </>
            )}
        </section>
    );
};

/* ── Banner ─────────────────────────────────────────────────── */

const ComplianceBlockingBanner: React.FC<{ contact: ContactDto }> = ({ contact }) => {
    const blocks = contact.blocks ?? [];
    const casesEnforcing = blocks.some(
        (b) => b.sourceType === 'COMPLIANCE' && b.blockedActionTypeCode === 'CASES' && isRowEnforcing(b)
    );
    const timecardsEnforcing = blocks.some(
        (b) => b.sourceType === 'COMPLIANCE' && b.blockedActionTypeCode === 'TIMECARDS' && isRowEnforcing(b)
    );
    const amlEnforcing = contact.isAmlTimecardsBlockActive ?? false;
    if (!casesEnforcing && !timecardsEnforcing && !amlEnforcing) {
        return null;
    }
    return (
        <Alert className="border-error bg-error-light text-fg flex items-center gap-2 py-2 pr-4 pl-3 font-semibold">
            {/* A wrapper, not a bare svg child: the kit alert lays a direct svg out as a grid column. */}
            <span className="flex w-6 shrink-0 justify-center">
                <ErrorOutlineIcon className="text-error size-4" />
            </span>
            <div className="flex flex-wrap items-center gap-2">
                <div className="text-body1 font-bold">
                    <FormattedMessage id="contacts.blocking.banner-title" />
                </div>
                {casesEnforcing && (
                    <StatusChip
                        label={<FormattedMessage id="contacts.blocking.banner-cases-blocked" />}
                        type="error"
                        size="small"
                    />
                )}
                {timecardsEnforcing && (
                    <StatusChip
                        label={<FormattedMessage id="contacts.blocking.banner-timecards-blocked" />}
                        type="error"
                        size="small"
                    />
                )}
                {amlEnforcing && (
                    <StatusChip
                        label={<FormattedMessage id="contacts.blocking.banner-aml-timecards-blocked" />}
                        type="error"
                        size="small"
                    />
                )}
            </div>
        </Alert>
    );
};

/* ── Compliance Block History ────────────────────────────────── */

const severityChipType: Record<ComplianceBlockingSeverity, 'critical' | 'error' | 'warning' | 'info'> = {
    [ComplianceBlockingSeverity.CRITICAL]: 'critical',
    [ComplianceBlockingSeverity.HIGH]: 'error',
    [ComplianceBlockingSeverity.MEDIUM]: 'warning',
    [ComplianceBlockingSeverity.LOW]: 'info',
};

const getHistoryStatusChip = (status: ComplianceBlockStatus) => {
    switch (status) {
        case ComplianceBlockStatus.ACTIVE:
            return { label: t('contacts.blocking.rule-status.active'), color: 'error' as const };
        case ComplianceBlockStatus.PENDING_CONFIRMATION:
            return { label: t('contacts.blocking.rule-status.pending-confirmation'), color: 'warning' as const };
        case ComplianceBlockStatus.RESOLVED:
            return { label: t('compliance.block-history.resolved'), color: 'success' as const };
    }
};

const HistoryRecordDetails: React.FC<{ record: ComplianceBlockHistoryDto; partyId: number }> = ({
    record,
    partyId,
}) => {
    const { openModal, closeModal } = useModal();
    const [acknowledgeBlock, { isLoading }] = useAcknowledgeComplianceBlockMutation();

    const isPending = record.status === ComplianceBlockStatus.PENDING_CONFIRMATION;

    const handleAcknowledge = () => {
        openModal(
            <AcknowledgeBlockModal
                rule={record}
                onCancel={closeModal}
                onConfirm={async (comment) => {
                    await acknowledgeBlock({ partyId, blockId: record.id, comment: comment || undefined });
                    closeModal();
                }}
            />
        );
    };

    return (
        <>
            <span className="text-caption text-fg-secondary block">
                <FormattedMessage id="compliance.block-history.triggered-at" />{' '}
                <FormattedDate value={record.triggeredAt} fallback="" withTime />
            </span>
            {record.resolvedAt && (
                <span className="text-caption text-fg-secondary block">
                    <FormattedMessage id="compliance.block-history.resolved-by" />{' '}
                    {record.resolvedByEmployee?.name ?? DEFAULT_VALUE}{' '}
                    <FormattedMessage id="compliance.block-history.on" />{' '}
                    <FormattedDate value={record.resolvedAt} fallback="" withTime />
                </span>
            )}
            {record.acknowledgedAt && (
                <span className="text-caption text-fg-secondary block">
                    <FormattedMessage id="compliance.block-history.acknowledged-by" />{' '}
                    {record.acknowledgedByEmployee?.name ?? DEFAULT_VALUE}{' '}
                    <FormattedMessage id="compliance.block-history.on" />{' '}
                    <FormattedDate value={record.acknowledgedAt} fallback="" withTime />
                </span>
            )}
            {record.comment && <span className="text-caption text-fg-secondary block italic">{record.comment}</span>}
            {isPending && (
                <Button
                    {...buttonStyle(
                        { variant: 'outlined', size: 'extraSmall' },
                        'text-caption mt-1 h-[22px] bg-transparent'
                    )}
                    onClick={handleAcknowledge}
                    disabled={isLoading}
                >
                    <FormattedMessage id="contacts.blocking.acknowledge" />
                </Button>
            )}
        </>
    );
};

const ComplianceBlockHistory: React.FC<{ partyId: number }> = ({ partyId }) => {
    const { data: history, isLoading } = useGetComplianceBlockHistoryQuery(String(partyId));
    const [hideResolved, setHideResolved] = useState(false);
    const hideResolvedId = useId();

    const sortedHistory = useMemo(() => {
        if (!history?.length) return [];
        return history.toSorted((a, b) => {
            const statusOrder = {
                [ComplianceBlockStatus.ACTIVE]: 0,
                [ComplianceBlockStatus.PENDING_CONFIRMATION]: 1,
                [ComplianceBlockStatus.RESOLVED]: 2,
            };
            const statusDiff = statusOrder[a.status] - statusOrder[b.status];
            if (statusDiff !== 0) return statusDiff;

            const ackA = a.acknowledgedAt ? new Date(a.acknowledgedAt).getTime() : 0;
            const ackB = b.acknowledgedAt ? new Date(b.acknowledgedAt).getTime() : 0;
            if (ackA !== ackB) return ackB - ackA;

            const resA = a.resolvedAt ? new Date(a.resolvedAt).getTime() : 0;
            const resB = b.resolvedAt ? new Date(b.resolvedAt).getTime() : 0;
            if (resA !== resB) return resB - resA;

            return new Date(b.triggeredAt).getTime() - new Date(a.triggeredAt).getTime();
        });
    }, [history]);

    const filteredHistory = useMemo(() => {
        if (!hideResolved) return sortedHistory;
        return sortedHistory.filter((r) => r.status !== ComplianceBlockStatus.RESOLVED);
    }, [sortedHistory, hideResolved]);

    if (isLoading) {
        return <Loader />;
    }

    return (
        <section className="border-divider bg-paper new:border-0 new:shadow-card new:outline-hidden overflow-hidden rounded-[10px] border">
            <div className="border-divider flex items-center justify-between border-b px-4 py-2">
                <h5 className="text-h5">
                    <FormattedMessage id="compliance.block-history" />
                </h5>
            </div>
            <div className="flex min-h-10 items-center gap-2 px-4 pt-2">
                <Checkbox
                    id={hideResolvedId}
                    checked={hideResolved}
                    onCheckedChange={(checked) => setHideResolved(checked)}
                />
                <label htmlFor={hideResolvedId} className="text-body1">
                    <FormattedMessage id="compliance.block-history.hide-resolved" />
                </label>
            </div>
            {!filteredHistory.length ? (
                <p className="text-body2 text-fg-secondary p-4">
                    <FormattedMessage id="compliance.no-block-history" />
                </p>
            ) : (
                <BlockHistoryTimeline records={filteredHistory} partyId={partyId} />
            )}
        </section>
    );
};

const BlockHistoryTimeline: React.FC<{ records: ComplianceBlockHistoryDto[]; partyId: number }> = ({
    records,
    partyId,
}) => (
    <HistoryTimeline
        entries={records.map((record) => ({
            key: record.id,
            tone: record.isActive ? 'error' : 'success',
            children: (
                <>
                    <div className="flex flex-wrap items-center gap-1">
                        <span className="text-body2 font-semibold">
                            {t(`compliance.blocking-rule.${record.ruleCode}`)}
                        </span>
                        <StatusChip
                            label={t(`contacts.blocking.severity.${record.severity.toLowerCase()}`)}
                            type={severityChipType[record.severity]}
                            size="small"
                        />
                        <StatusChip
                            label={getHistoryStatusChip(record.status).label}
                            type={getHistoryStatusChip(record.status).color}
                            size="small"
                        />
                    </div>
                    <HistoryRecordDetails record={record} partyId={partyId} />
                </>
            ),
        }))}
    />
);

/* ── Compliance Checks ───────────────────────────────────────── */

interface ComplianceCheksProps {
    contact: ContactDto;
    refetchContact: () => Promise<unknown>;
}

const ComplianceChecks: React.FC<ComplianceCheksProps> = ({ contact, refetchContact }) => {
    const { openModal, closeModal } = useModal();

    const data = useMemo(() => {
        const requiredChecks: Row[] = [];
        const kycChecks: Row[] = [];
        const additionalChecks: Row[] = [];

        contact.complianceChecks?.forEach((check) => {
            const row: Row = {
                label: check.complianceCheckName,
                content: <ComplianceCheckContent {...check} />,
                icon: <StatusIcon type={getCheckStatusChipType(check.complianceCheckStatusTypeCode)} />,
                noDivider: true,
                button: (
                    <ComplianceCheckEditButton
                        check={check}
                        onClick={() =>
                            openModal(
                                <ComplianceCheckModal
                                    refetchContact={refetchContact}
                                    onClose={closeModal}
                                    contact={contact}
                                    check={check}
                                    type={'edit'}
                                />
                            )
                        }
                    />
                ),
            };
            if (check.isRequiredField) {
                requiredChecks.push(row);
            }
            if (!check.isRequiredField && !check.isManuallyEntered) {
                kycChecks.push(row);
            }
            if (!check.isRequiredField && check.isManuallyEntered) {
                additionalChecks.push(row);
            }
        });
        const requiredCheckHeader: Row = {
            content: (
                <h5 className="text-h5">
                    <FormattedMessage id={'compliance.required-check'} />
                </h5>
            ),
            label: null,
        };

        const kycHeader: Row = {
            content: (
                <h5 className="text-h5">
                    <FormattedMessage id={'compliance.kyc-check'} />
                </h5>
            ),
            label: null,
        };

        const additionalChecksHeader: Row = {
            content: (
                <h5 className="text-h5">
                    <FormattedMessage id={'compliance.additional-check'} />
                </h5>
            ),
            label: null,
            button: (
                <ComplianceCheckAddButton
                    onClick={() =>
                        openModal(
                            <ComplianceCheckModal
                                contact={contact}
                                refetchContact={refetchContact}
                                onClose={closeModal}
                                type={'add'}
                            />
                        )
                    }
                />
            ),
        };

        const noData: Row = {
            content: <NoData />,
            label: null,
        };

        return [
            requiredCheckHeader,
            ...(requiredChecks.length ? requiredChecks : [noData]),
            kycHeader,
            ...(kycChecks.length ? kycChecks : [noData]),
            additionalChecksHeader,
            ...(additionalChecks.length ? additionalChecks : [noData]),
        ];
    }, [contact, openModal, refetchContact, closeModal]);

    return <GridItemList titleId="compliance.compliance-check" size={12} data={data} />;
};

const ExpiryIndicator: React.FC<
    Pick<
        PartyComplianceCheckDto,
        'complianceCheckStatusTypeCode' | 'isApproachingExpiry' | 'daysUntilExpiry' | 'expiresAt'
    >
> = (props) => {
    const effectiveFormat = useEffectiveDateFormat();
    if (props.complianceCheckStatusTypeCode === CheckStatusType.PASSED && props.isApproachingExpiry) {
        return (
            <StatusChip
                label={<FormattedMessage id="compliance.expires-in-days" values={{ days: props.daysUntilExpiry }} />}
                type="warning"
                size="small"
                className="mt-1"
            />
        );
    }
    if (props.complianceCheckStatusTypeCode === CheckStatusType.EXPIRED && props.expiresAt) {
        const formattedDate = format(new Date(props.expiresAt), effectiveFormat);
        return (
            <StatusChip
                label={<FormattedMessage id="compliance.expired-on" values={{ date: formattedDate }} />}
                type="error"
                size="small"
                className="mt-1"
            />
        );
    }
    return null;
};

const ComplianceCheckContent: React.FC<PartyComplianceCheckDto> = (props) => {
    const value = props.valueText;

    if (
        CheckType.UBO_DETERMINED === props.complianceCheckTypeCode ||
        CheckType.AML_FILLED === props.complianceCheckTypeCode ||
        CheckType.SERVICE_AGREEMENT_PRESENT === props.complianceCheckTypeCode
    ) {
        const skipValueText =
            (CheckType.SERVICE_AGREEMENT_PRESENT === props.complianceCheckTypeCode ||
                CheckType.AML_FILLED === props.complianceCheckTypeCode) &&
            props.resolvedByEntityName;

        return (
            <div className="flex flex-col">
                {!skipValueText && <div>{value}</div>}
                <div>
                    <RecordLink
                        className={LINK_CLASS}
                        to={`/contract/${props.resolvedByEntityId}`}
                        label={props.resolvedByEntityName ?? ''}
                    />
                </div>
                <ExpiryIndicator {...props} />
            </div>
        );
    }

    return (
        <div className="flex flex-col">
            <div>{value}</div>
            <ExpiryIndicator {...props} />
        </div>
    );
};

interface ComplianceCheckEditButtonProps {
    check: PartyComplianceCheckDto;
    onClick: () => void;
}

const ComplianceCheckEditButton: React.FC<ComplianceCheckEditButtonProps> = ({ check, onClick }) => {
    if (check.isManuallyResolvable) {
        return (
            <Button {...buttonStyle({ variant: 'outlined', size: 'extraSmall' }, 'bg-transparent')} onClick={onClick}>
                <FormattedMessage id="edit" />
            </Button>
        );
    }
    return null;
};

const ComplianceCheckAddButton: React.FC<{ onClick: () => void }> = ({ onClick }) => {
    return (
        <Button {...buttonStyle({ variant: 'outlined', size: 'extraSmall' }, 'bg-transparent')} onClick={onClick}>
            <FormattedMessage id="add" />
        </Button>
    );
};
